import React, { useState, useEffect, useCallback, useRef } from 'react';
import { formatDateTime } from '@framework/utils/dateUtils';
import {
  UtensilsCrossed,
  Eye,
  Trash2,
  Image,
  Filter,
  Edit,
  Save,
  X,
  Plus,
  Minus,
  ArrowUp,
  ArrowDown,
  Loader2,
  ExternalLink,
  Clock,
  Users,
  ChefHat,
  Tag,
  BookOpen,
  Sparkles,
  Check,
  Calculator,
} from 'lucide-react';
import { useTheme } from '@framework/contexts/ThemeContext';
import DataTable, { Table } from '@framework/components/DataTable';
import BaseModal from '@framework/components/BaseModal';
import ActionButton from '@framework/components/ActionButton';
import FormActions from '@framework/components/FormActions';
import ErrorAlert from '@framework/components/ErrorAlert';

const PAGE_SIZE = 20;

const SOURCE_TYPES = [
  { value: '', label: 'All Sources' },
  { value: 'book', label: 'Book' },
  { value: 'url', label: 'URL' },
  { value: 'chat', label: 'Chat' },
  { value: 'photo', label: 'Photo' },
  { value: 'manual', label: 'Manual' },
  { value: 'social', label: 'Social' },
  { value: 'scan', label: 'Scan' },
  { value: 'video', label: 'Video' },
];

const RecipesPage = () => {
  const { apiService: api } = useTheme();
  const [recipes, setRecipes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const sentinelRef = useRef(null);
  const [loadKey, setLoadKey] = useState(0);

  // Debounce search
  const searchTimeout = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
      setRecipes([]);
      setHasMore(true);
    }, 400);
    return () => clearTimeout(searchTimeout.current);
  }, [searchTerm]);

  const loadRecipes = useCallback(async () => {
    try {
      const isInitial = page === 0;
      if (isInitial) setLoading(true);
      else setLoadingMore(true);
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      const result = await api.getRecipes(params);
      const batch = result.recipes;
      if (isInitial) {
        setRecipes(batch);
      } else {
        setRecipes(prev => [...prev, ...batch]);
      }
      setTotalCount(result.total);
      setHasMore(batch.length === PAGE_SIZE);
      setError(null);
    } catch (err) {
      console.error('Error loading recipes:', err);
      setError('Failed to load recipes');
      if (page === 0) setRecipes([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [api, page, debouncedSearch, loadKey]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  // Client-side source filter (backend doesn't support source_type filter on user recipes)
  const filteredRecipes = sourceFilter
    ? recipes.filter(r => (r.source_type || '').toLowerCase() === sourceFilter)
    : recipes;

  const handleRefresh = useCallback(() => {
    setRecipes([]);
    setHasMore(true);
    setPage(0);
    setLoadKey(k => k + 1);
  }, []);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          setPage(p => p + 1);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  const handleDelete = async (id) => {
    try {
      await api.deleteRecipe(id);
      setDeleteConfirm(null);
      setRecipes(prev => prev.filter(r => r.id !== id));
      setTotalCount(prev => prev - 1);
    } catch (err) {
      console.error('Error deleting recipe:', err);
      setError('Failed to delete recipe');
    }
  };

  const handleRowClick = (recipe) => {
    setSelectedRecipe(recipe.id);
  };

  const getDifficultyColor = (d) => {
    switch (d) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const columns = [
    {
      header: '',
      accessor: 'image',
      className: 'w-12',
      cellClassName: '!py-2',
      render: (r) => r.image_url ? (
        <img src={r.image_url} alt="" className="w-10 h-10 rounded-md object-cover" />
      ) : (
        <div className="w-10 h-10 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <UtensilsCrossed className="w-4 h-4 text-gray-400" />
        </div>
      )
    },
    {
      header: 'Name',
      accessor: 'name',
      render: (r) => (
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">{r.name}</div>
          {r.source_type && (
            <span className="text-xs text-gray-400 capitalize">{r.source_type}</span>
          )}
        </div>
      )
    },
    {
      header: 'Category',
      accessor: 'category',
      render: (r) => <span className="text-sm text-gray-600 dark:text-gray-300">{r.category || '—'}</span>
    },
    {
      header: 'Cuisine',
      accessor: 'cuisine',
      render: (r) => <span className="text-sm text-gray-600 dark:text-gray-300">{r.cuisine || '—'}</span>
    },
    {
      header: 'Difficulty',
      accessor: 'difficulty',
      render: (r) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${getDifficultyColor(r.difficulty)}`}>
          {r.difficulty || '—'}
        </span>
      )
    },
    {
      header: 'Servings',
      accessor: 'servings',
      render: (r) => <span className="text-sm text-gray-600 dark:text-gray-300">{r.servings}</span>
    },
    {
      header: 'Created',
      accessor: 'created_at',
      render: (r) => <span className="text-sm text-gray-500">{formatDateTime(r.created_at)}</span>
    },
    {
      header: 'Actions',
      accessor: 'actions',
      className: 'text-right',
      render: (r) => (
        <div className="flex justify-end space-x-1" onClick={(e) => e.stopPropagation()}>
          <ActionButton icon={Eye} onClick={() => setSelectedRecipe(r.id)} variant="secondary" size="xs" />
          <ActionButton icon={Trash2} onClick={() => setDeleteConfirm(r)} variant="danger" size="xs" />
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <DataTable
        title="Recipes"
        subtitle={`${totalCount} total recipes`}
        icon={UtensilsCrossed}
        iconColor="text-orange-500"
        loading={loading}
        error={error}
        onRefresh={handleRefresh}
        searchProps={{
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
          placeholder: 'Search recipes...'
        }}
        filters={[
          {
            type: 'select',
            icon: Filter,
            value: sourceFilter,
            onChange: (e) => setSourceFilter(e.target.value),
            options: SOURCE_TYPES,
          },
        ]}
      >
        <Table columns={columns} data={filteredRecipes} rowKey="id" onRowClick={handleRowClick} />

        {filteredRecipes.length === 0 && !loading && (
          <div className="p-12 text-center">
            <UtensilsCrossed className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No recipes found</h3>
            <p className="text-gray-600 dark:text-gray-400">Try adjusting your search or filters.</p>
          </div>
        )}

        {/* Infinite scroll sentinel */}
        <div ref={sentinelRef} className="h-1" />
        {loadingMore && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        )}
      </DataTable>

      {selectedRecipe && (
        <RecipeDetailModal
          recipeId={selectedRecipe}
          api={api}
          onClose={() => setSelectedRecipe(null)}
          onDeleted={() => {
            const deletedId = selectedRecipe;
            setSelectedRecipe(null);
            setRecipes(prev => prev.filter(r => r.id !== deletedId));
            setTotalCount(prev => prev - 1);
          }}
          onUpdated={handleRefresh}
        />
      )}

      {deleteConfirm && (
        <BaseModal isOpen={true} onClose={() => setDeleteConfirm(null)} title="Delete Recipe">
          <div className="p-6 space-y-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
            </p>
            <FormActions
              onCancel={() => setDeleteConfirm(null)}
              onSubmit={() => handleDelete(deleteConfirm.id)}
              submitText="Delete"
              variant="danger"
            />
          </div>
        </BaseModal>
      )}
    </div>
  );
};


// ============================================================================
// Recipe Detail / Edit Modal
// ============================================================================

const RecipeDetailModal = ({ recipeId, api, onClose, onDeleted, onUpdated }) => {
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [imageStatus, setImageStatus] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const pollRef = useRef(null);

  // Edit form state
  const [form, setForm] = useState({});

  // Book source info (for source_type === 'book')
  const [bookSource, setBookSource] = useState(null);

  // AI Improve Notes state
  const [notesProvider, setNotesProvider] = useState('groq');
  const [notesImproving, setNotesImproving] = useState(false);
  const [improvedNotes, setImprovedNotes] = useState(null);
  const [notesStatus, setNotesStatus] = useState('');
  const notesPollRef = useRef(null);

  // AI Generate Description state
  const [descProvider, setDescProvider] = useState('claude');
  const [descGenerating, setDescGenerating] = useState(false);
  const [generatedDesc, setGeneratedDesc] = useState(null);
  const [descStatus, setDescStatus] = useState('');
  const descPollRef = useRef(null);

  // USDA Nutrition Calculator state
  const [nutritionCalculating, setNutritionCalculating] = useState(false);
  const [nutritionResult, setNutritionResult] = useState(null);
  const [nutritionStatus, setNutritionStatus] = useState('');
  const nutritionPollRef = useRef(null);

  const loadRecipe = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getRecipe(recipeId);
      setRecipe(data);
      setError(null);

      // If recipe came from a book import, fetch source info
      if (data.source_type === 'book') {
        try {
          const source = await api.getRecipeSourceBook(recipeId);
          setBookSource(source);
        } catch {
          // Not found or not admin — ignore silently
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load recipe');
    } finally {
      setLoading(false);
    }
  }, [api, recipeId]);

  useEffect(() => {
    loadRecipe();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (notesPollRef.current) clearInterval(notesPollRef.current);
      if (nutritionPollRef.current) clearInterval(nutritionPollRef.current);
      if (descPollRef.current) clearInterval(descPollRef.current);
    };
  }, [loadRecipe]);

  const startEdit = () => {
    setForm({
      name: recipe.name || '',
      description: recipe.description || '',
      servings: recipe.servings || 4,
      difficulty: recipe.difficulty || 'medium',
      category: recipe.category || '',
      cuisine: recipe.cuisine || '',
      prep_time_minutes: recipe.prep_time_minutes || '',
      cook_time_minutes: recipe.cook_time_minutes || '',
      notes: recipe.notes || '',
      tags: (recipe.tags || []).join(', '),
      ingredients: (recipe.ingredients || []).map(ing => ({
        name: ing.name || ing.ingredient_name || '',
        quantity: ing.quantity || '',
        unit: ing.unit || '',
        preparation: ing.preparation || '',
        ingredient_group: ing.ingredient_group || '',
        is_optional: ing.is_optional || false,
      })),
      instructions: (recipe.instructions || []).map(inst => ({
        step_number: inst.step_number,
        instruction: inst.instruction,
      })),
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        name: form.name,
        description: form.description || null,
        servings: parseInt(form.servings) || 4,
        difficulty: form.difficulty,
        category: form.category || null,
        cuisine: form.cuisine || null,
        prep_time_minutes: form.prep_time_minutes ? parseInt(form.prep_time_minutes) : null,
        cook_time_minutes: form.cook_time_minutes ? parseInt(form.cook_time_minutes) : null,
        notes: form.notes || null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        ingredients: form.ingredients.map((ing, idx) => ({
          name: ing.name,
          quantity: ing.quantity ? parseFloat(ing.quantity) : null,
          unit: ing.unit || null,
          preparation: ing.preparation || null,
          ingredient_group: ing.ingredient_group || null,
          is_optional: ing.is_optional,
        })),
        instructions: form.instructions.map((inst, idx) => ({
          step_number: idx + 1,
          instruction: inst.instruction,
        })),
      };
      await api.updateRecipe(recipeId, payload);
      setEditing(false);
      await loadRecipe();
      onUpdated();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateImage = async () => {
    try {
      setImageGenerating(true);
      setImageStatus('Starting image generation...');
      const result = await api.generateImage(recipeId, selectedProvider || null);
      const taskId = result.task_id;

      pollRef.current = setInterval(async () => {
        try {
          const status = await api.getImageStatus(taskId);
          if (status.status === 'completed') {
            clearInterval(pollRef.current);
            setImageGenerating(false);
            setImageStatus('');
            await loadRecipe();
            onUpdated();
          } else if (status.status === 'failed') {
            clearInterval(pollRef.current);
            setImageGenerating(false);
            setImageStatus('');
            setError(status.error || 'Image generation failed');
          } else {
            setImageStatus(status.message || 'Generating image...');
          }
        } catch (err) {
          clearInterval(pollRef.current);
          setImageGenerating(false);
          setImageStatus('');
          setError('Failed to check image status');
        }
      }, 2000);
    } catch (err) {
      setImageGenerating(false);
      setImageStatus('');
      setError(err.message || 'Failed to start image generation');
    }
  };

  const handleDelete = async () => {
    try {
      await api.deleteRecipe(recipeId);
      onDeleted();
    } catch (err) {
      setError(err.message || 'Failed to delete recipe');
    }
  };

  const handleImproveNotes = async () => {
    try {
      setNotesImproving(true);
      setNotesStatus('Starting...');
      setImprovedNotes(null);
      const result = await api.improveNotes(recipeId, notesProvider || null);
      const taskId = result.task_id;

      notesPollRef.current = setInterval(async () => {
        try {
          const status = await api.getImproveNotesStatus(taskId);
          if (status.status === 'completed') {
            clearInterval(notesPollRef.current);
            setNotesImproving(false);
            setNotesStatus('');
            if (status.improved_notes) {
              setImprovedNotes(status.improved_notes);
            } else {
              setError('No improved notes returned');
            }
          } else if (status.status === 'failed') {
            clearInterval(notesPollRef.current);
            setNotesImproving(false);
            setNotesStatus('');
            setError(status.error || 'Notes improvement failed');
          } else {
            setNotesStatus(status.message || 'Improving notes...');
          }
        } catch {
          clearInterval(notesPollRef.current);
          setNotesImproving(false);
          setNotesStatus('');
          setError('Failed to check notes status');
        }
      }, 2000);
    } catch (err) {
      setNotesImproving(false);
      setNotesStatus('');
      setError(err.message || 'Failed to start notes improvement');
    }
  };

  const handleApplyNotes = async () => {
    try {
      setSaving(true);
      await api.updateRecipe(recipeId, { notes: improvedNotes });
      setImprovedNotes(null);
      await loadRecipe();
      onUpdated();
    } catch (err) {
      setError(err.message || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  // AI Generate Description
  const handleGenerateDescription = async () => {
    try {
      setDescGenerating(true);
      setDescStatus('Starting...');
      setGeneratedDesc(null);
      const result = await api.generateDescription(recipeId, descProvider || null);
      const taskId = result.task_id;

      descPollRef.current = setInterval(async () => {
        try {
          const status = await api.getGenerateDescriptionStatus(taskId);
          if (status.status === 'completed') {
            clearInterval(descPollRef.current);
            setDescGenerating(false);
            setDescStatus('');
            if (status.description) {
              setGeneratedDesc(status.description);
            } else {
              setError('No description returned');
            }
          } else if (status.status === 'failed') {
            clearInterval(descPollRef.current);
            setDescGenerating(false);
            setDescStatus('');
            setError(status.error || 'Description generation failed');
          } else {
            setDescStatus(status.message || 'Generating description...');
          }
        } catch {
          clearInterval(descPollRef.current);
          setDescGenerating(false);
          setDescStatus('');
          setError('Failed to check description status');
        }
      }, 2000);
    } catch (err) {
      setDescGenerating(false);
      setDescStatus('');
      setError(err.message || 'Failed to start description generation');
    }
  };

  const handleApplyDescription = async () => {
    try {
      setSaving(true);
      await api.updateRecipe(recipeId, { description: generatedDesc });
      setGeneratedDesc(null);
      await loadRecipe();
      onUpdated();
    } catch (err) {
      setError(err.message || 'Failed to save description');
    } finally {
      setSaving(false);
    }
  };

  // USDA Nutrition Calculator
  const handleCalculateNutrition = async () => {
    try {
      setNutritionCalculating(true);
      setNutritionStatus('Starting...');
      setNutritionResult(null);
      const result = await api.calculateNutrition(recipeId);
      const taskId = result.task_id;

      nutritionPollRef.current = setInterval(async () => {
        try {
          const status = await api.getNutritionStatus(taskId);
          if (status.status === 'completed') {
            clearInterval(nutritionPollRef.current);
            setNutritionCalculating(false);
            setNutritionStatus('');
            if (status.success) {
              setNutritionResult(status);
            } else {
              setError(status.error || 'Nutrition calculation failed');
            }
          } else if (status.status === 'failed') {
            clearInterval(nutritionPollRef.current);
            setNutritionCalculating(false);
            setNutritionStatus('');
            setError(status.error || 'Nutrition calculation failed');
          } else {
            setNutritionStatus(status.message || 'Calculating nutrition...');
          }
        } catch {
          clearInterval(nutritionPollRef.current);
          setNutritionCalculating(false);
          setNutritionStatus('');
          setError('Failed to check nutrition status');
        }
      }, 2000);
    } catch (err) {
      setNutritionCalculating(false);
      setNutritionStatus('');
      setError(err.message || 'Failed to start nutrition calculation');
    }
  };

  const handleApplyNutrition = async () => {
    if (!nutritionResult?.per_serving) return;
    try {
      setSaving(true);
      const ps = nutritionResult.per_serving;
      await api.updateRecipe(recipeId, {
        calories: ps.calories,
        protein_g: ps.protein_g,
        carbs_g: ps.carbs_g,
        fat_g: ps.fat_g,
        fiber_g: ps.fiber_g,
        sugar_g: ps.sugar_g,
      });
      setNutritionResult(null);
      await loadRecipe();
      onUpdated();
    } catch (err) {
      setError(err.message || 'Failed to save nutrition');
    } finally {
      setSaving(false);
    }
  };

  // Ingredient helpers
  const addIngredient = () => {
    setForm(f => ({
      ...f,
      ingredients: [...f.ingredients, { name: '', quantity: '', unit: '', preparation: '', ingredient_group: '', is_optional: false }]
    }));
  };
  const removeIngredient = (idx) => {
    setForm(f => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));
  };
  const updateIngredient = (idx, field, value) => {
    setForm(f => ({
      ...f,
      ingredients: f.ingredients.map((ing, i) => i === idx ? { ...ing, [field]: value } : ing)
    }));
  };

  // Instruction helpers
  const addInstruction = () => {
    setForm(f => ({
      ...f,
      instructions: [...f.instructions, { step_number: f.instructions.length + 1, instruction: '' }]
    }));
  };
  const removeInstruction = (idx) => {
    setForm(f => ({
      ...f,
      instructions: f.instructions.filter((_, i) => i !== idx).map((inst, i) => ({ ...inst, step_number: i + 1 }))
    }));
  };
  const updateInstruction = (idx, value) => {
    setForm(f => ({
      ...f,
      instructions: f.instructions.map((inst, i) => i === idx ? { ...inst, instruction: value } : inst)
    }));
  };
  const moveInstruction = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= form.instructions.length) return;
    setForm(f => {
      const arr = [...f.instructions];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...f, instructions: arr.map((inst, i) => ({ ...inst, step_number: i + 1 })) };
    });
  };

  return (
    <BaseModal isOpen={true} onClose={onClose} title={recipe?.name || 'Recipe Details'} size="3xl">
      <div className="p-6 space-y-6">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        )}
        {error && <ErrorAlert error={error} variant="error" />}

        {recipe && !editing && (
          <>
            {/* Image + Actions */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                {recipe.image_url ? (
                  <img src={recipe.image_url} alt={recipe.name} className="w-48 h-48 rounded-lg object-cover" />
                ) : (
                  <div className="w-48 h-48 rounded-lg bg-gray-100 dark:bg-gray-700 flex flex-col items-center justify-center space-y-2">
                    <UtensilsCrossed className="w-12 h-12 text-gray-400" />
                    <span className="text-xs text-gray-400">No image</span>
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{recipe.name}</h3>
                  {recipe.description && !generatedDesc && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{recipe.description}</p>
                  )}
                  {!recipe.description && !generatedDesc && (
                    <p className="text-sm text-gray-400 italic mt-1">No description.</p>
                  )}
                  {generatedDesc && (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={generatedDesc}
                        onChange={e => setGeneratedDesc(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-orange-300 dark:border-orange-600 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500"
                      />
                      <div className="flex gap-2">
                        <ActionButton icon={Check} onClick={handleApplyDescription} variant="primary" size="sm" disabled={saving} loading={saving}>
                          {saving ? 'Saving...' : 'Apply Description'}
                        </ActionButton>
                        <ActionButton icon={X} onClick={() => setGeneratedDesc(null)} variant="secondary" size="sm">
                          Discard
                        </ActionButton>
                      </div>
                    </div>
                  )}
                  {!generatedDesc && (
                    <div className="flex items-center gap-2 mt-1">
                      <select
                        value={descProvider}
                        onChange={e => setDescProvider(e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        disabled={descGenerating}
                      >
                        <option value="claude">Claude</option>
                        <option value="openai">GPT</option>
                        <option value="gemini">Gemini</option>
                        <option value="groq">Groq</option>
                        <option value="xai">Grok</option>
                        <option value="mistral">Mistral</option>
                        <option value="deepseek">DeepSeek</option>
                      </select>
                      <ActionButton
                        icon={descGenerating ? Loader2 : Sparkles}
                        onClick={handleGenerateDescription}
                        variant="secondary"
                        size="xs"
                        disabled={descGenerating}
                        loading={descGenerating}
                      >
                        {descGenerating ? (descStatus || 'Generating...') : 'Generate Description'}
                      </ActionButton>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 text-sm text-gray-600 dark:text-gray-300">
                  {recipe.category && (
                    <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded capitalize">{recipe.category}</span>
                  )}
                  {recipe.cuisine && (
                    <span className="px-2 py-1 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">{recipe.cuisine}</span>
                  )}
                  {recipe.difficulty && (
                    <span className={`px-2 py-1 rounded capitalize ${
                      recipe.difficulty === 'easy' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                      recipe.difficulty === 'hard' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                      'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                    }`}>{recipe.difficulty}</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                  {recipe.servings && (
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{recipe.servings} servings</span>
                  )}
                  {recipe.prep_time_minutes && (
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Prep: {recipe.prep_time_minutes}m</span>
                  )}
                  {recipe.cook_time_minutes && (
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Cook: {recipe.cook_time_minutes}m</span>
                  )}
                  {recipe.source_type && (
                    <span className="capitalize">Source: {recipe.source_type}</span>
                  )}
                </div>

                {recipe.source_url && (
                  <a href={recipe.source_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                    <ExternalLink className="w-3 h-3" /> Source URL
                  </a>
                )}

                {bookSource && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                    <BookOpen className="w-3.5 h-3.5 text-orange-500" />
                    <span>
                      Imported from: {bookSource.filename}
                      {bookSource.page_number && <span className="text-gray-400"> (p.{bookSource.page_number})</span>}
                    </span>
                    {bookSource.pdf_url && (
                      <a href={bookSource.pdf_url} target="_blank" rel="noopener noreferrer"
                        className="text-orange-600 dark:text-orange-400 hover:underline ml-1">
                        <ExternalLink className="w-3 h-3 inline" />
                      </a>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 items-center">
                  <ActionButton icon={Edit} onClick={startEdit} variant="secondary" size="sm">Edit</ActionButton>
                  <select
                    value={selectedProvider}
                    onChange={e => setSelectedProvider(e.target.value)}
                    className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Default (xAI)</option>
                    <option value="google">Google Imagen 4</option>
                    <option value="openai">OpenAI DALL-E 3</option>
                    <option value="luma">Luma Photon</option>
                    <option value="xai">xAI Grok Imagine</option>
                  </select>
                  <ActionButton
                    icon={imageGenerating ? Loader2 : Image}
                    onClick={handleGenerateImage}
                    variant="primary"
                    size="sm"
                    disabled={imageGenerating}
                    loading={imageGenerating}
                  >
                    {imageGenerating ? (imageStatus || 'Generating...') : 'Generate Image'}
                  </ActionButton>
                  <ActionButton icon={Trash2} onClick={() => setDeleteConfirm(true)} variant="danger" size="sm">Delete</ActionButton>
                </div>
              </div>
            </div>

            {/* Tags */}
            {recipe.tags && recipe.tags.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  <Tag className="w-4 h-4" /> Tags
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {recipe.tags.map((tag, i) => (
                    <span key={i} className="px-2 py-0.5 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Nutrition */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Nutrition (per serving)</h4>
                <ActionButton
                  icon={nutritionCalculating ? Loader2 : Calculator}
                  onClick={handleCalculateNutrition}
                  variant="secondary"
                  size="xs"
                  disabled={nutritionCalculating}
                  loading={nutritionCalculating}
                >
                  {nutritionCalculating ? (nutritionStatus || 'Calculating...') : 'Calculate (USDA)'}
                </ActionButton>
              </div>

              {/* Existing nutrition data */}
              {!nutritionResult && (recipe.calories || recipe.protein_g || recipe.carbs_g || recipe.fat_g) && (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  <NutritionBox label="Calories" value={recipe.calories || '—'} unit="kcal" />
                  <NutritionBox label="Protein" value={recipe.protein_g || '—'} unit="g" />
                  <NutritionBox label="Carbs" value={recipe.carbs_g || '—'} unit="g" />
                  <NutritionBox label="Fat" value={recipe.fat_g || '—'} unit="g" />
                  <NutritionBox label="Fiber" value={recipe.fiber_g || '—'} unit="g" />
                  <NutritionBox label="Sugar" value={recipe.sugar_g || '—'} unit="g" />
                </div>
              )}
              {!nutritionResult && !recipe.calories && !recipe.protein_g && !recipe.carbs_g && !recipe.fat_g && (
                <p className="text-sm text-gray-400 italic">No nutrition data. Click "Calculate (USDA)" to compute from ingredients.</p>
              )}

              {/* Calculated nutrition result */}
              {nutritionResult && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    <NutritionBox label="Calories" value={nutritionResult.per_serving.calories} unit="kcal" />
                    <NutritionBox label="Protein" value={nutritionResult.per_serving.protein_g} unit="g" />
                    <NutritionBox label="Carbs" value={nutritionResult.per_serving.carbs_g} unit="g" />
                    <NutritionBox label="Fat" value={nutritionResult.per_serving.fat_g} unit="g" />
                    <NutritionBox label="Fiber" value={nutritionResult.per_serving.fiber_g} unit="g" />
                    <NutritionBox label="Sugar" value={nutritionResult.per_serving.sugar_g} unit="g" />
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {nutritionResult.matched_count}/{nutritionResult.total_ingredients} ingredients matched to USDA database
                  </div>

                  {/* Per-ingredient breakdown table */}
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-x-auto">
                    <table className="w-full text-xs text-gray-900 dark:text-gray-100">
                      <thead>
                        <tr className="text-left text-[10px] text-gray-500 dark:text-gray-400 uppercase">
                          <th className="px-2 py-1.5">Ingredient</th>
                          <th className="px-2 py-1.5">USDA Match</th>
                          <th className="px-2 py-1.5 text-right">Grams</th>
                          <th className="px-2 py-1.5 text-right">Calories</th>
                          <th className="px-2 py-1.5 text-right">Protein</th>
                          <th className="px-2 py-1.5 text-right">Carbs</th>
                          <th className="px-2 py-1.5 text-right">Fat</th>
                          <th className="px-2 py-1.5 text-right">Fiber</th>
                          <th className="px-2 py-1.5 text-right">Sugar</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {nutritionResult.ingredient_breakdown.map((item, i) => (
                          <tr key={i} className={!item.fdc_id ? 'bg-amber-50 dark:bg-amber-900/20' : ''}>
                            <td className="px-2 py-1.5">
                              <span>{item.quantity} {item.name}</span>
                            </td>
                            <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400 max-w-[200px] truncate" title={item.usda_match}>
                              {item.usda_match || <span className="text-amber-500 dark:text-amber-400 italic">no match</span>}
                              {item.confidence === 'low' && <span className="text-amber-500 dark:text-amber-400 ml-1" title="Low confidence match">?</span>}
                            </td>
                            <td className="px-2 py-1.5 text-right">{item.estimated_grams || '—'}</td>
                            <td className="px-2 py-1.5 text-right">{item.calories ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right">{item.protein_g ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right">{item.carbs_g ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right">{item.fat_g ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right">{item.fiber_g ?? '—'}</td>
                            <td className="px-2 py-1.5 text-right">{item.sugar_g ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex gap-2">
                    <ActionButton icon={Check} onClick={handleApplyNutrition} variant="primary" size="sm" disabled={saving} loading={saving}>
                      {saving ? 'Saving...' : 'Apply Nutrition'}
                    </ActionButton>
                    <ActionButton icon={X} onClick={() => setNutritionResult(null)} variant="secondary" size="sm">
                      Discard
                    </ActionButton>
                  </div>
                </div>
              )}
            </div>

            {/* Ingredients */}
            {recipe.ingredients && recipe.ingredients.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Ingredients ({recipe.ingredients.length})
                </h4>
                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 uppercase">
                        <th className="px-3 py-2">Qty</th>
                        <th className="px-3 py-2">Unit</th>
                        <th className="px-3 py-2">Ingredient</th>
                        <th className="px-3 py-2">Prep</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {recipe.ingredients.map((ing, i) => (
                        <tr key={i}>
                          <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{ing.quantity || '—'}</td>
                          <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{ing.unit || '—'}</td>
                          <td className="px-3 py-1.5 font-medium text-gray-900 dark:text-white">
                            {ing.name || ing.ingredient_name}
                            {ing.is_optional && <span className="text-xs text-gray-400 ml-1">(optional)</span>}
                          </td>
                          <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">{ing.preparation || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Instructions */}
            {recipe.instructions && recipe.instructions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Instructions ({recipe.instructions.length} steps)
                </h4>
                <ol className="space-y-3">
                  {recipe.instructions
                    .sort((a, b) => a.step_number - b.step_number)
                    .map((inst, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 flex items-center justify-center text-sm font-bold">
                        {inst.step_number}
                      </span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 pt-1">{inst.instruction}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Notes + AI Improve */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Notes</h4>
                <div className="flex items-center gap-2">
                  <select
                    value={notesProvider}
                    onChange={e => setNotesProvider(e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={notesImproving}
                  >
                    <option value="claude">Claude</option>
                    <option value="openai">GPT</option>
                    <option value="gemini">Gemini</option>
                    <option value="groq">Groq</option>
                    <option value="xai">Grok</option>
                    <option value="mistral">Mistral</option>
                    <option value="deepseek">DeepSeek</option>
                  </select>
                  <ActionButton
                    icon={notesImproving ? Loader2 : Sparkles}
                    onClick={handleImproveNotes}
                    variant="secondary"
                    size="xs"
                    disabled={notesImproving}
                    loading={notesImproving}
                  >
                    {notesImproving ? (notesStatus || 'Improving...') : 'Improve with AI'}
                  </ActionButton>
                </div>
              </div>
              {recipe.notes && !improvedNotes && (
                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{recipe.notes}</p>
              )}
              {!recipe.notes && !improvedNotes && (
                <p className="text-sm text-gray-400 italic">No notes yet. Use "Improve with AI" to generate notes.</p>
              )}
              {improvedNotes && (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={improvedNotes}
                    onChange={e => setImprovedNotes(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 text-sm border border-orange-300 dark:border-orange-600 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500"
                  />
                  <div className="flex gap-2">
                    <ActionButton icon={Check} onClick={handleApplyNotes} variant="primary" size="sm" disabled={saving} loading={saving}>
                      {saving ? 'Saving...' : 'Apply Notes'}
                    </ActionButton>
                    <ActionButton icon={X} onClick={() => setImprovedNotes(null)} variant="secondary" size="sm">
                      Discard
                    </ActionButton>
                  </div>
                </div>
              )}
            </div>

            {/* Delete confirmation */}
            {deleteConfirm && (
              <div className="border border-red-200 dark:border-red-800 rounded-lg p-4 bg-red-50 dark:bg-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-300 mb-3">
                  Are you sure you want to delete <strong>{recipe.name}</strong>?
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setDeleteConfirm(false)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700">
                    Cancel
                  </button>
                  <button onClick={handleDelete}
                    className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">
                    Delete
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* EDIT MODE */}
        {recipe && editing && (
          <div className="space-y-5">
            {/* Basic fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input type="text" value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea value={form.description} rows={2}
                  onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <input type="text" value={form.category}
                  onChange={(e) => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cuisine</label>
                <input type="text" value={form.cuisine}
                  onChange={(e) => setForm(f => ({ ...f, cuisine: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Servings</label>
                <input type="number" min="1" value={form.servings}
                  onChange={(e) => setForm(f => ({ ...f, servings: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Difficulty</label>
                <select value={form.difficulty}
                  onChange={(e) => setForm(f => ({ ...f, difficulty: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prep Time (min)</label>
                <input type="number" min="0" value={form.prep_time_minutes}
                  onChange={(e) => setForm(f => ({ ...f, prep_time_minutes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cook Time (min)</label>
                <input type="number" min="0" value={form.cook_time_minutes}
                  onChange={(e) => setForm(f => ({ ...f, cook_time_minutes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (comma-separated)</label>
                <input type="text" value={form.tags}
                  onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g. quick, healthy, comfort food"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea value={form.notes} rows={2}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>

            {/* Ingredients editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Ingredients</h4>
                <button type="button" onClick={addIngredient}
                  className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {form.ingredients.map((ing, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input type="text" placeholder="Qty" value={ing.quantity}
                      onChange={(e) => updateIngredient(idx, 'quantity', e.target.value)}
                      className="w-16 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <input type="text" placeholder="Unit" value={ing.unit}
                      onChange={(e) => updateIngredient(idx, 'unit', e.target.value)}
                      className="w-20 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <input type="text" placeholder="Ingredient name" value={ing.name}
                      onChange={(e) => updateIngredient(idx, 'name', e.target.value)}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <input type="text" placeholder="Prep" value={ing.preparation}
                      onChange={(e) => updateIngredient(idx, 'preparation', e.target.value)}
                      className="w-28 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button type="button" onClick={() => removeIngredient(idx)}
                      className="p-1 text-red-500 hover:text-red-700">
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Instructions</h4>
                <button type="button" onClick={addInstruction}
                  className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 dark:text-orange-400">
                  <Plus className="w-3 h-3" /> Add Step
                </button>
              </div>
              <div className="space-y-2">
                {form.instructions.map((inst, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 flex items-center justify-center text-sm font-bold mt-1">
                      {idx + 1}
                    </span>
                    <textarea value={inst.instruction} rows={2}
                      onChange={(e) => updateInstruction(idx, e.target.value)}
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <div className="flex flex-col gap-0.5">
                      <button type="button" onClick={() => moveInstruction(idx, -1)} disabled={idx === 0}
                        className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveInstruction(idx, 1)} disabled={idx === form.instructions.length - 1}
                        className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => removeInstruction(idx)}
                        className="p-0.5 text-red-500 hover:text-red-700">
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Save / Cancel */}
            <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setEditing(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-50 transition-colors">
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

const NutritionBox = ({ label, value, unit }) => (
  <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 rounded-lg">
    <div className="text-lg font-bold text-gray-900 dark:text-white">{value}<span className="text-xs text-gray-400 ml-0.5">{unit}</span></div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

export { RecipeDetailModal };
export default RecipesPage;
